import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, Flag, Trophy, BarChart3, Settings, ShieldCheck } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import Landing from "@/pages/Landing";
import AppDisclaimer from "@/components/AppDisclaimer";

const navItems = [
  { title: "Home", path: "/", icon: Home },
  { title: "Races", path: "/races", icon: Flag },
  { title: "Leagues", path: "/leagues", icon: Trophy },
  { title: "Results", path: "/results", icon: BarChart3 },
];

export default function AppLayout() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { user, loading, profile } = useAuth();

  if (loading) return null;

  if (!user) return <Landing />;

  return (
    <div className="flex min-h-screen w-full flex-col bg-background">
      {/* Desktop sidebar */}
      {!isMobile && (
        <aside className="fixed inset-y-0 left-0 z-40 flex w-48 flex-col border-r border-border bg-sidebar">
          <div className="flex h-16 items-center gap-2 px-6">
            <span className="text-2xl font-black italic tracking-tight text-primary">SEND IT</span>
          </div>
          <nav className="flex flex-1 flex-col gap-1 px-3 pt-2">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                activeClassName="bg-sidebar-accent text-primary"
              >
                <item.icon className="h-5 w-5" />
                <span>{item.title}</span>
              </NavLink>
            ))}
            <div className="my-2 border-t border-border" />
            <NavLink
              to="/profile"
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-sidebar-foreground/60 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              activeClassName="bg-sidebar-accent text-primary"
            >
              <Settings className="h-5 w-5" />
              <span>Profile</span>
            </NavLink>
            {profile?.is_admin && (
              <NavLink
                to="/admin"
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-sidebar-foreground/60 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                activeClassName="bg-sidebar-accent text-primary"
              >
                <ShieldCheck className="h-5 w-5" />
                <span>Admin</span>
              </NavLink>
            )}
          </nav>
        </aside>
      )}

      {/* Main content */}
      <main
        className={cn(
          "flex-1",
          !isMobile && "ml-48",
          isMobile && "pb-20"
        )}
      >
        {/* Mobile gear icon */}
        {isMobile && (
          <button
            onClick={() => navigate("/profile")}
            className="absolute top-6 right-4 z-30 flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:text-foreground transition-colors"
          >
            <Settings className="h-5 w-5" />
          </button>
        )}
        <Outlet />
        <AppDisclaimer />
      </main>

      {/* Mobile bottom nav */}
      {isMobile && (
        <nav className="fixed inset-x-0 bottom-0 z-40 flex h-16 items-center justify-around border-t border-border bg-background/90 backdrop-blur-lg">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              className="flex flex-col items-center gap-1 px-3 py-1 text-muted-foreground transition-colors"
              activeClassName="text-primary"
            >
              <item.icon className="h-5 w-5" />
              <span className="text-[10px] font-medium">{item.title}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
